import { cn } from 'component-library';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UnitEnum } from '@/api/rest/resources/types/units';

type SliderProps = {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
  className?: string;
  showMinMax?: boolean;
  unit?: UnitEnum;
};

export const Slider = ({
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  className,
  showMinMax = false,
  unit,
}: SliderProps) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleShowTooltip = useCallback(() => {
    clearTimeout(hideTimeoutRef.current);
    setShowTooltip(true);
  }, []);

  const handleHideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setShowTooltip(false), 500);
  }, []);

  return (
    <div className={cn('relative flex flex-row justify-center items-center', className)}>
      {showTooltip && (
        <div
          className='absolute -top-6 -translate-x-1/2 bg-primary-100 text-white-100 text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none'
          style={{ left: `${((value - min) / (max - min)) * 100}%` }}
        >
          {value}
          {unit && t(`global.ui.unit.${unit}`)}
        </div>
      )}
      {showMinMax && (
        <span className='mr-1 text-xs text-text-secondary'>
          {min}
          {unit && t(`global.ui.unit.${unit}`)}
        </span>
      )}
      <input
        type='range'
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          handleShowTooltip();
          onChange(Number(e.target.value));
        }}
        onMouseDown={handleShowTooltip}
        onMouseUp={handleHideTooltip}
        onTouchStart={handleShowTooltip}
        onTouchEnd={handleHideTooltip}
        className={cn(
          'w-full h-1 bg-primary-12 rounded-lg appearance-none cursor-pointer hover:bg-primary-30 transition-colors',
          '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-100 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:border-none',
          '[&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-100 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-none',
        )}
      />
      {showMinMax && (
        <span className='ml-1 text-xs text-text-secondary'>
          {max}
          {unit && t(`global.ui.unit.${unit}`)}
        </span>
      )}
    </div>
  );
};
